import {
  useMediaQuery,
  useTheme,
  Typography,
  type TypographyProps,
} from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'

type TypographyClassNames = keyof ReturnType<typeof useStyles>['classes']

const useStyles = makeStyles()({
  headingStyles: {
    fontFamily: ['Figtree', 'sans-serif'].join(','),
    fontWeight: 700,
  },
  h1: {
    fontSize: '24px',
    lineHeight: '32px',
  },
  h2: {
    fontSize: '20px',
    lineHeight: '28px',
  },
  h3: {
    fontSize: '18px',
    lineHeight: '24px',
  },
  h4: {
    fontSize: '16px',
    lineHeight: '24px',
  },
  body1: {
    fontSize: '16px',
    lineHeight: '24px',
    whiteSpace: 'pre-wrap',
  },
  bold: {
    fontWeight: 700,
  },
  underline: {
    textDecoration: 'underline',
  },
  smallText: {
    fontSize: '14px',
    lineHeight: '22px',
  },
  extraSmallText: {
    fontSize: '12px',
    lineHeight: '20px',
  },
})

export type TypographyComponentProps = TypographyProps & {
  // to be able to change the semantic element as supported by MUI
  component?: React.ElementType
  mobileSize?: 'h2' | 'h3' | 'h4' | 'smallText' | 'extraSmallText'
}

const getTypographyComponent =
  (
    BaseComponent: React.FC<TypographyProps>,
    classNames: TypographyClassNames[],
    variant?: TypographyProps['variant'],
  ): React.FC<TypographyComponentProps> =>
  (props) => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const isDownSM = useMediaQuery(theme.breakpoints.down('sm'))

    const propsBase = {
      ...props,
      className: cx([
        ...classNames.map((c) => classes[c]),
        props.className,
        props.mobileSize && isDownSM && classes[props.mobileSize],
      ]),
    }

    if (variant) {
      return <BaseComponent variant={variant} {...propsBase} />
    }

    return <BaseComponent {...propsBase} />
  }

export const Heading1 = getTypographyComponent(
  Typography,
  ['headingStyles', 'h1'],
  'h1',
)

export const Heading1Bold = getTypographyComponent(
  Typography,
  ['headingStyles', 'h1'],
  'h1',
)

export const Heading2 = getTypographyComponent(
  Typography,
  ['headingStyles', 'h2'],
  'h2',
)

export const Heading3 = getTypographyComponent(
  Typography,
  ['headingStyles', 'h3'],
  'h3',
)

export const Heading4 = getTypographyComponent(
  Typography,
  ['headingStyles', 'h4', 'bold'],
  'h4',
)

export const TextBody = getTypographyComponent(Typography, ['body1'], 'body1')
export const TextBodyBold = getTypographyComponent(TextBody, ['bold'])
export const TextBodyUnderline = getTypographyComponent(TextBody, ['underline'])
export const TextBodyBoldUnderline = getTypographyComponent(TextBodyBold, [
  'underline',
])

export const SmallText = getTypographyComponent(TextBody, ['smallText'])
export const SmallTextBold = getTypographyComponent(SmallText, ['bold'])
export const SmallTextUnderline = getTypographyComponent(SmallText, [
  'underline',
])
export const SmallTextBoldUnderline = getTypographyComponent(SmallTextBold, [
  'underline',
])

export const ExtraSmallText = getTypographyComponent(TextBody, [
  'extraSmallText',
])
export const ExtraSmallTextBold = getTypographyComponent(ExtraSmallText, [
  'bold',
])
export const ExtraSmallTextUnderline = getTypographyComponent(ExtraSmallText, [
  'underline',
])
